"use client";

/**
 * `<PricingTeaser>` — three mini pricing cards linking to /pricing.
 *
 * RESTORED 2026-06-10: the Phase-5 landing rebuild dropped this section;
 * recovered from git `ae78b37` and kept byte-compatible with the current
 * marketing design (primitives + Btn + Reveal + design tokens).
 *
 * Pricing locked per dispatch Phase 3:
 * - Reports: Free  (Pro $9.99/mo · Premium $19.99/mo)
 * - Compass: $29   (Premium $79/mo)  [FLAGSHIP]
 * - Simulate: $12  (3 sims · $35/10 sims · $75/25 sims)
 */

import Link from "next/link";
import { Container, SectionHead } from "../primitives";
import { Btn } from "../btn";
import { Reveal } from "../reveal";
import { ArrowR } from "../icons";

interface Tier {
  name: string;
  tag: string;
  price: string;
  sub: string;
  flagship?: boolean;
}

const TIERS: Tier[] = [
  {
    name: "WTM Reports",
    tag: "On-demand",
    price: "Free",
    sub: "Pro $9.99/mo · Premium $19.99/mo",
  },
  {
    name: "WTM Compass",
    tag: "Continuous",
    price: "$29",
    sub: "/mo · Premium $79/mo",
    flagship: true,
  },
  {
    name: "WTM Simulate",
    tag: "Per-stock",
    price: "$12",
    sub: "/ 3 sims · $35 / 10 sims · $75 / 25 sims",
  },
];

export function PricingTeaser() {
  return (
    <section style={{ padding: "100px 0" }}>
      <Container max={1320}>
        <Reveal>
          <SectionHead
            eyebrow="Pricing"
            title="Simple, transparent pricing."
            sub="Three products, priced independently. Start free with Reports. Pick what fits your workflow."
            align="center"
            className="mb-14"
          />
        </Reveal>

        <div
          className="grid grid-cols-1 md:grid-cols-3"
          style={{ gap: 16 }}
        >
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 100}>
              <PricingMiniCard {...t} />
            </Reveal>
          ))}
        </div>

        <Reveal delay={400}>
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <Btn
              variant="secondary"
              size="lg"
              href="/pricing"
              icon={<ArrowR />}
              analyticsLocation="home_pricing_teaser_view_full"
            >
              View full pricing
            </Btn>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function PricingMiniCard({ name, tag, price, sub, flagship }: Tier) {
  return (
    <Link
      href="/pricing"
      style={{
        display: "block",
        background: flagship
          ? "linear-gradient(180deg, var(--color-surface-2) 0%, var(--color-surface) 100%)"
          : "var(--color-surface)",
        border: `1px solid ${flagship ? "rgba(59,130,246,0.25)" : "var(--color-line)"}`,
        borderRadius: 14,
        padding: 28,
        cursor: "pointer",
        transition: "transform 0.2s",
        position: "relative",
        height: "100%",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "none";
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--color-dim)",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
          }}
        >
          {tag}
        </div>
        {flagship ? (
          <span
            style={{
              fontSize: 9,
              padding: "3px 7px",
              borderRadius: 4,
              background: "rgba(59,130,246,0.12)",
              color: "var(--color-blue-2)",
              letterSpacing: "0.14em",
              fontWeight: 600,
              fontFamily: "var(--font-mono)",
            }}
          >
            FLAGSHIP
          </span>
        ) : null}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 600,
          color: "var(--color-text)",
          marginTop: 8,
          letterSpacing: "-0.015em",
        }}
      >
        {name}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 6,
          marginTop: 22,
        }}
      >
        <span
          style={{
            fontSize: 38,
            fontWeight: 600,
            color: "var(--color-text)",
            letterSpacing: "-0.025em",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {price}
        </span>
      </div>
      <div style={{ fontSize: 12, color: "var(--color-dim)", marginTop: 4 }}>
        {sub}
      </div>
    </Link>
  );
}
